/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Html, html, View } from 'rune-ts';
import klass from './ButtonView.module.scss';
import { font } from '@services/MarppleShop/shared/typography';
import { HeartLineIcon } from '@services/MarppleShop/shared/components/atoms/Icon';
import { classes } from '@services/MarppleShop/shared/util';

type TIconName = 'heart_line';

const icon_htmls_by_name: Record<TIconName, Html> = {
  heart_line: HeartLineIcon,
};

type TElementType = 'button' | 'a';

const DEFAULT_BUTTON_TAG = 'button' as const;

type TSize = 80 | 56 | 48 | 40 | 32 | 24;

type TColor =
  | 'Primary_A'
  | 'Primary_B'
  | 'Secondary-A'
  | 'Secondary-B'
  | 'Custom-A'
  | 'Custom-B'
  | 'Custom-C';

const button_classes_by_size: Record<TSize, string> = {
  '80': klass.size_80!,
  '56': klass.size_56!,
  '48': klass.size_48!,
  '40': klass.size_40!,
  '32': klass.size_32!,
  '24': klass.size_24!,
};

const button_classes_by_color: Record<TColor, string> = {
  Primary_A: klass.style_primary_a!,
  Primary_B: klass.style_primary_b!,
  'Secondary-A': klass.style_secondary_a!,
  'Secondary-B': klass.style_secondary_b!,
  'Custom-A': klass.style_custom_a!,
  'Custom-B': klass.style_custom_b!,
  'Custom-C': klass.style_custom_c!,
};

type THtmlAttributes = {
  // ButtonHTMLAttributes<HTMLButtonElement>
  button: {
    className: string;
    type: 'submit' | 'reset' | 'button';
    disabled: boolean;
  };
  // AnchorHTMLAttributes<HTMLAnchorElement>
  a: {
    className: string;
    href: string;
    target: '_self' | '_blank' | '_parent' | '_top' | '_unfencedTop';
    dataset: Partial<{
      postMessage: string;
    }>;
  };
};

export type TButtonViewProps<TTag extends TElementType = typeof DEFAULT_BUTTON_TAG> = {
  size: TSize;
  color: TColor;
  label?: string;
  icon_leading?: TIconName;
  icon_trailing?: TIconName;
  as?: TTag;
  onClick?: (event: MouseEvent) => void;
} & Partial<THtmlAttributes[TTag]>;

/**
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=18448-55847&m=dev
 */
export class ButtonView<TTag extends TElementType = typeof DEFAULT_BUTTON_TAG> extends View<
  TButtonViewProps<TTag>
> {
  protected override onRender() {
    if (this.data.onClick) {
      this.addEventListener('click', this.data.onClick);
    }
  }

  protected override template({
    size,
    color,
    label,
    icon_leading,
    icon_trailing,
    as,
    ...html_attributes
  }: TButtonViewProps<TTag>) {
    const icon_leading_html = icon_leading ? icon_htmls_by_name[icon_leading] : null;

    const icon_trailing_html = icon_trailing ? icon_htmls_by_name[icon_trailing] : null;

    const is_bold = color === 'Primary_A' || color === 'Primary_B' || color === 'Secondary-A';

    const label_classes_by_size: Record<TSize, string> = {
      '80': font(is_bold ? '24_bold' : '24_medium'),
      '56': font(is_bold ? '16_bold' : '16_medium'),
      '48': font(is_bold ? '16_bold' : '16_medium'),
      '40': font(is_bold ? '14_bold' : '14_medium'),
      '32': font(is_bold ? '14_bold' : '14_medium'),
      '24': font(is_bold ? '12_bold' : '12_medium'),
    };

    const inner_html = html`${icon_leading_html}
    ${label ? html`<span class="${label_classes_by_size[size]}">${label}</span>` : null} ${icon_trailing_html}`;

    if (as === 'a') {
      const { className, href, target, dataset } = html_attributes as Partial<THtmlAttributes['a']>;

      return html`<a
        class="${classes(button_classes_by_size[size], button_classes_by_color[color], className)}"
        ${href ? html`href="${href}"` : null}
        ${target ? html`target="${target}"` : null}
        ${dataset?.postMessage ? html`data-post-message="${dataset.postMessage}"` : null}
        >${inner_html}</a
      >`;
    }

    const { className, type, disabled } = html_attributes as Partial<THtmlAttributes['button']>;

    return html`<button
      class="${classes(button_classes_by_size[size], button_classes_by_color[color], className)}"
      ${type ? html`type="${type}"` : null}
      ${disabled ? html`disabled` : null}
    >
      ${inner_html}
    </button>`;
  }
}
